import { trechoDaSessao } from "./sessao";
/**
 * Catálogo de sistemas do grupo SERCAMP & NX ENERGY e navegação com login único.
 *
 * Todos os sistemas rodam localmente em portas diferentes. Ao abrir um sistema
 * pela área de trabalho, levamos o usuário logado pela URL (#sso=...) e, quando
 * o usuário clica num módulo específico, também o levamos direto para a seção
 * correspondente.
 *
 * Há dois estilos de navegação interna entre os sistemas:
 *   • TAB-based (#tab=chave): sistemas SERCAMP padrão (RH, DP, Qualidade,
 *     Almoxarifado, Planejamento, Operacional, Laboratório). Lêem o hash #tab=.
 *   • PATH-based (/rota): sistemas com roteador próprio (Comercial e Proposta =
 *     React Router, Relatorios = SvelteKit). O módulo carrega um `path` e
 *     abrimos `url + path`.
 *
 * Faixa de portas exclusiva da SERCAMP para NÃO colidir com o projeto
 * "SISTEMA GERAL SERCAMP & NX ENERGY" (3000/3001/3002/8080):
 *   • Frontends (o que o Portal abre): 4001–4010
 *   • Backends internos: Proposta 4108, Relatorios 4109; segurança Proposta 4208
 * Assim nenhum dos sistemas integrados colide entre si na mesma VPS.
 */
export type SystemId =
  | "rh"
  | "dp"
  | "qualidade"
  | "almoxarifado"
  | "planejamento"
  | "operacional"
  | "comercial"
  | "proposta"
  | "gestao_comercial"
  | "relatorios"
  | "laboratorio"
  | "colaborador"
  | "suprimentos"
  | "desenvolvedor"
  | "ponto";

export type SystemIcon =
  | "users"
  | "doc"
  | "shield"
  | "box"
  | "calendar"
  | "gauge"
  | "briefcase"
  | "quote"
  | "kanban"
  | "report"
  | "flask"
  | "clock"
  | "idcard";

/**
 * Um módulo (seção) de um sistema.
 *  - `key`  é a chave usada no deep-link por aba (#tab=) dos sistemas tab-based.
 *  - `path` (opcional) é a rota absoluta dos sistemas path-based (ex.: "/historico").
 *    Quando presente, abrimos `url + path` em vez de `#tab=`.
 */
export interface SystemModule {
  label: string;
  key: string;
  path?: string;
}

export interface SystemDef {
  id: SystemId;
  nome: string;
  descricao: string;
  url: string;
  icon: SystemIcon;
  modules: SystemModule[];
}

/** Porta de cada sistema (usada apenas em desenvolvimento, em localhost). */
const PORTS: Record<SystemId, number> = {
  rh: 4001,
  dp: 4002,
  qualidade: 4003,
  almoxarifado: 4004,
  planejamento: 4005,
  operacional: 4006,
  comercial: 4007,
  proposta: 4008,
  // Servida pelo próprio app do Propostas (rotas /gestao): mesma porta.
  gestao_comercial: 4008,
  relatorios: 4009,
  laboratorio: 4010,
  colaborador: 4011,
  desenvolvedor: 4012,
  suprimentos: 4013,
  ponto: 4014,
};

/**
 * Subdomínio de cada sistema EM PRODUÇÃO (atrás do nginx, com domínio próprio).
 * O Portal fica no apex (ex.: jktype.shop) e cada sistema num subdomínio
 * (ex.: rh.jktype.shop). O nginx faz proxy reverso de cada subdomínio para a
 * porta interna correspondente — ver deploy/nginx/jktype.shop.conf.
 */
const SUBDOMAINS: Record<SystemId, string> = {
  rh: "rh",
  dp: "dp",
  qualidade: "qualidade",
  almoxarifado: "almoxarifado",
  planejamento: "planejamento",
  operacional: "operacional",
  comercial: "comercial",
  proposta: "proposta",
  // Mesmo subdomínio do Propostas: a Gestão Comercial é a área /gestao dele
  // (nenhum DNS, nginx ou certificado novo).
  gestao_comercial: "proposta",
  relatorios: "relatorios",
  laboratorio: "laboratorio",
  colaborador: "colaborador",
  // O id interno é "desenvolvedor", mas o subdomínio publicado é
  // "desenvolvimento" (foi esse o registro criado no DNS, em 22/09/2026).
  desenvolvedor: "desenvolvimento",
  suprimentos: "suprimentos",
  // Nome visível "Colaboradores"; o id interno segue "ponto" (ver sistemaPonto/server/index.ts).
  ponto: "colaboradores",
};

/**
 * Resolve a URL base de um sistema, funcionando tanto local quanto na VPS sem
 * precisar recompilar para cada ambiente:
 *   • Em localhost/127.0.0.1 (desenvolvimento) → http://localhost:<porta>.
 *   • Em produção (qualquer outro domínio) → <protocolo>//<subdomínio>.<domínio>,
 *     derivando o domínio-raiz do próprio endereço aberto. Assim, se o domínio
 *     mudar (ex.: de jktype.shop para outro), nada precisa ser alterado aqui.
 *
 * IMPORTANTE: a navegação roda no NAVEGADOR do usuário, por isso não podemos
 * usar "localhost" em produção (apontaria para a máquina do próprio usuário).
 */
const host = (id: SystemId): string => {
  if (typeof window !== "undefined") {
    const h = window.location.hostname;
    const isLocal = h === "localhost" || h === "127.0.0.1" || h === "::1";
    if (!isLocal) {
      // Domínio-raiz = hostname atual sem o "www." (o Portal fica no apex).
      const root = h.replace(/^www\./, "");
      return `${window.location.protocol}//${SUBDOMAINS[id]}.${root}`;
    }
  }
  return `http://localhost:${PORTS[id]}`;
};

const mod = (label: string, key: string, path?: string): SystemModule => ({
  label,
  key,
  path,
});

export const SYSTEMS: SystemDef[] = [
  {
    id: "rh",
    nome: "Recursos Humanos",
    descricao: "Gestão de pessoas, do recrutamento ao desenvolvimento.",
    url: host("rh"),
    icon: "users",
    modules: [
      mod("Dashboard", "dashboard"),
      mod("Colaboradores", "colaboradores"),
      mod("Plano de Ação", "treinamentos"),
      mod("Pesquisa de Clima", "engajamento"),
      mod("Contratações", "contratacoes"),
      mod("Triagem de Currículo", "triagem"),
      mod("Entrevistas", "entrevistas"),
      mod("Demissões", "demissoes"),
      mod("Promoções", "promocoes"),
      mod("Avaliações", "avaliacoes"),
      mod("Avaliação de Desempenho", "desempenho"),
      mod("Endomarketing", "endomarketing"),
      mod("Cargos e Salários", "cargos"),
    ],
  },
  {
    id: "dp",
    nome: "Departamento Pessoal",
    descricao: "Rotinas trabalhistas, benefícios e folha.",
    url: host("dp"),
    icon: "doc",
    modules: [
      mod("Dashboard", "dashboard"),
      mod("Colaboradores", "colaboradores"),
      mod("Benefícios", "beneficios"),
      mod("Documentos", "documentos"),
      mod("Folha de Ponto", "folha_ponto"),
      mod("Horas Extras", "horas_extras"),
      mod("Rescisões", "rescisoes"),
      mod("Férias", "ferias"),
      mod("Plano de Ação", "treinamentos"),
    ],
  },
  {
    id: "qualidade",
    nome: "Qualidade",
    descricao: "Gestão da qualidade, conformidade e melhoria contínua.",
    url: host("qualidade"),
    icon: "shield",
    modules: [
      mod("Dashboard", "dashboard"),
      mod("Não Conformidades", "nao_conformidades"),
      mod("Ações Corretivas", "acoes_corretivas"),
      mod("Auditorias", "auditorias"),
      mod("Satisfação do Cliente", "satisfacao_cliente"),
      mod("Retrabalho", "retrabalho"),
      mod("Satisfação e Retrabalho por Equipe", "desempenho"),
      mod("Conformidade (Normas)", "conformidade_normas"),
      mod("Documentos", "documentos"),
      mod("Gestores por Setor", "gestores_setor"),
    ],
  },
  {
    /* ALMOXARIFADO — a OPERAÇÃO do estoque físico: o material que entra, sai,
       é conferido e é contado. Divide o banco (e o saldo) com o Suprimentos,
       que cuida da COMPRA — ver db/estoque-compartilhado.sql. */
    id: "almoxarifado",
    nome: "Almoxarifado",
    descricao: "Estoque físico: entrada e saída, termo de retirada, recebimento e inventário.",
    url: host("almoxarifado"),
    icon: "box",
    modules: [
      mod("Dashboard", "dashboard"),
      mod("Itens", "itens"),
      mod("Alertas de Estoque", "alertas"),
      mod("Recebimento de Materiais", "recebimentos"),
      mod("Movimentações", "movimentacoes"),
      mod("Inventários", "inventarios"),
      mod("Solicitações", "solicitacoes"),
      mod("Documentos", "documentos"),
    ],
  },
  {
    /* SUPRIMENTOS — a cadeia de COMPRA do começo ao fim: cadastro de material e
       fornecedor, requisição, cotação, nota fiscal e impostos. O ESTOQUE é o
       mesmo do Almoxarifado (mesmo banco, mesmas tabelas): conferir uma nota
       aqui faz o saldo subir lá, e um termo de retirada lá baixa o saldo daqui.
       Ver db/estoque-compartilhado.sql. */
    id: "suprimentos",
    nome: "Suprimentos",
    descricao: "Compras, cotações, notas fiscais e impostos — sobre o estoque do Almoxarifado.",
    url: host("suprimentos"),
    icon: "box",
    modules: [
      mod("Dashboard", "dashboard"),
      mod("Cadastro de Materiais", "materiais"),
      mod("Cadastro de Fornecedores", "fornecedores"),
      mod("Compras", "compras"),
      mod("Orçamentos", "orcamentos"),
      mod("Entrada de Notas Fiscais", "notas"),
      mod("Entrada e Saída", "estoque"),
      mod("Impostos", "impostos"),
    ],
  },
  {
    /* PONTO — o único sistema voltado ao QUADRO INTEIRO, e não a um setor: é
       onde cada colaborador registra a própria jornada, pelo celular, com
       localização, foto e a hora carimbada pelo servidor.

       Usa o MESMO banco do DP (`sercamp`): a batida cai direto na `folha_ponto`
       que o Departamento Pessoal já usa, sem cópia nem sincronização para
       desencontrar. A régua do cálculo (jornada, tolerância, feriados, banco de
       horas) é mantida pelo DP — quem bate o ponto não configura o ponto. */
    id: "ponto",
    nome: "Colaboradores",
    descricao: "Ponto, folhas, horas extras, banco de horas, documentos e dados de cada colaborador.",
    url: host("ponto"),
    icon: "idcard",
    /* Sem módulos: o app é de tela única por aba, feito para o celular, e não
       tem deep-link por `#tab=` como os sistemas administrativos. */
    modules: [],
  },
  {
    id: "planejamento",
    nome: "Planejamento",
    descricao: "Obras, forecast, frota e equipamentos de ensaio.",
    url: host("planejamento"),
    icon: "calendar",
    modules: [
      mod("Dashboard", "dashboard"),
      mod("Agenda", "agenda"),
      mod("Atividades", "atividades"),
      mod("Satisfação", "satisfacao"),
      mod("Retrabalho", "retrabalho"),
      mod("Veículos", "veiculos"),
      mod("Equipamentos de Ensaio", "equipamentos"),
      mod("Equipe", "colaboradores"),
    ],
  },
  {
    id: "operacional",
    nome: "Gestão da Operação",
    descricao: "Indicadores operacionais em tempo real: SST, treinamentos e ensaios.",
    url: host("operacional"),
    icon: "gauge",
    modules: [
      mod("Dashboard", "dashboard"),
      mod("Agenda", "agenda"),
      mod("Treinamentos", "treinamentos"),
      mod("Retrabalho", "retrabalho"),
      mod("Acidentes / SST", "acidentes"),
      mod("Satisfação", "satisfacao"),
      mod("Veículos", "veiculos"),
      mod("Manutenção de Veículos", "manutencao_veiculos"),
      mod("Relatório de Inspeção", "relatorio_inspecao"),
      mod("Equipamentos de Ensaio", "equipamentos"),
      mod("Avaliar Folga", "avaliar_folga"),
      mod("Liberações", "liberacoes"),
      mod("Aprovar Agendamento", "aprovar_agendamento"),
      mod("Relatórios", "painel_relatorios"),
      mod("Almoxarifado", "almoxarifado"),
      mod("Equipe", "colaboradores"),
      mod("Usuários", "usuarios"),
    ],
  },
  {
    id: "comercial",
    nome: "Comercial (SDR)",
    descricao: "SDR: prospecção, interações, carteira de clientes e priorização inteligente.",
    url: host("comercial"),
    icon: "briefcase",
    // Sistema com roteador próprio (React Router): módulos path-based (/rota).
    modules: [
      mod("Dashboard", "dashboard", "/dashboard"),
      mod("Priorização (IA)", "priorizacao", "/dashboard/priorizacao"),
      mod("Interações", "interacoes", "/dashboard/interacoes"),
      mod("Carteira / Clientes", "clientes", "/dashboard/clientes"),
      mod("Manutenção Programada", "manutencao", "/dashboard/manutencao"),
      mod("Desligamentos ANEEL", "aneel", "/dashboard/aneel"),
      mod("Novas Indústrias", "industrias", "/dashboard/industrias"),
      mod("Relatórios", "relatorios", "/dashboard/relatorios"),
      mod("Carteiras (ADM)", "carteira", "/dashboard/carteira"),
    ],
  },
  {
    /* Gestão das propostas pelos vendedores: Kanban (Proposta | Negociação |
       Stand by | Conclusão), Resumo e Clientes com follow-up. É a área
       /gestao do app do Propostas (mesmo banco das propostas) — por isso a
       URL leva o "/gestao" e os módulos são relativos a ela. Os dados de
       cliente são os mesmos do Comercial (SDR): o que um atualiza o outro vê. */
    id: "gestao_comercial",
    nome: "Gestão Comercial",
    descricao: "Gestão de propostas por etapa, resumo de valores, clientes e follow-up dos vendedores.",
    url: `${host("gestao_comercial")}/gestao`,
    icon: "kanban",
    modules: [
      mod("Gestão de Propostas", "kanban", "/"),
      mod("Resumo", "resumo", "/resumo"),
      mod("Clientes", "clientes", "/clientes"),
    ],
  },
  {
    id: "proposta",
    nome: "Propostas",
    descricao: "Geração e acompanhamento de propostas comerciais e orçamentos.",
    url: host("proposta"),
    icon: "quote",
    modules: [
      mod("Painel", "dashboard", "/"),
      mod("Nova Proposta (Chat)", "nova_proposta", "/nova-proposta"),
      mod("Histórico", "historico", "/historico-propostas"),
      mod("Modelos de Escopo", "modelos_escopo", "/modelos-escopo"),
      mod("Tabela de Preços", "tabela_valores", "/tabela-valores"),
      mod("Ordens de Serviço", "numero_sequencia", "/numero-sequencia"),
      mod("Usuários", "usuarios", "/usuarios"),
      mod("Configurações", "configuracoes", "/configuracoes"),
    ],
  },
  {
    id: "relatorios",
    nome: "Relatórios",
    descricao: "Geração e mesclagem de relatórios técnicos (.docx) por OS.",
    url: host("relatorios"),
    icon: "report",
    modules: [
      mod("Fotográfico", "fotografico", "/relatorios/fotografico"),
      mod("SPDA", "spda", "/relatorios/spda"),
      mod("RDO de Montagem", "rdo", "/relatorios/rdo"),
      mod("Relatório Técnico", "tecnico", "/relatorios/tecnico"),
      mod("Relatório de Gastos", "gastos", "/relatorios/gastos"),
      mod("Ordens de Serviço", "os", "/admin/os"),
      mod("Banco de Relatórios", "banco_relatorios", "/admin/relatorios"),
      mod("Mesclar Relatórios", "mesclar", "/admin/mesclar"),
      mod("Usuários", "usuarios", "/admin/usuarios"),
    ],
  },
  {
    id: "laboratorio",
    nome: "Laboratório",
    descricao: "Análise de óleo e ensaios laboratoriais.",
    url: host("laboratorio"),
    icon: "flask",
    modules: [
      mod("Dashboard", "dashboard"),
      mod("Análises", "analises"),
      mod("Material do cliente", "material"),
      mod("Quebra de frasco/seringa", "quebras"),
      mod("Aprovação de relatório", "aprovacao"),
      mod("Relatórios", "historico"),
      mod("Equipamentos", "equipamentos"),
      mod("Limites normativos", "limites"),
    ],
  },
  {
    id: "colaborador",
    nome: "Operacional",
    descricao: "App do colaborador de campo: minha agenda, inspeção de veículos e solicitar folga.",
    url: host("colaborador"),
    icon: "calendar",
    modules: [
      mod("Minha Agenda", "agenda"),
      mod("Inspeção de Veículos", "inspecao_veiculo"),
      mod("Solicitar Folga", "solicitar_folga"),
    ],
  },
  {
    /* DESENVOLVIMENTO — o painel de quem cuida dos sistemas. O card só aparece
       para quem tiver "desenvolvedor" liberado no Portal (o sistema recusa o
       login de qualquer outra conta, ver sistemaDesenvolvedor/server/acesso.ts). */
    id: "desenvolvedor",
    nome: "Desenvolvimento",
    descricao:
      "Solicitações dos sistemas, agenda, lembretes, avisos de atualização e a auditoria de tudo.",
    url: host("desenvolvedor"),
    icon: "shield",
    modules: [
      mod("Painel", "painel"),
      mod("Solicitações", "solicitacoes"),
      mod("Agenda", "agenda"),
      mod("Lembretes", "lembretes"),
      mod("Atualizações", "atualizacoes"),
      mod("Auditoria", "auditoria"),
    ],
  },
];

/**
 * Navega para um sistema levando o usuário logado (login único) e, se informado,
 * abrindo direto numa seção.
 *  - Módulos tab-based: anexa `#sso=usuario&tab=chave`.
 *  - Módulos path-based (module.path): abre `url + path` (+ `#sso=usuario`).
 */
export function goToSystem(
  url: string,
  user: string | null,
  module?: SystemModule
) {
  const sso = user ? `sso=${encodeURIComponent(user)}${trechoDaSessao()}` : "";

  // Sistema com roteador próprio (path-based): abre a rota diretamente.
  if (module?.path) {
    const hash = sso ? `#${sso}` : "";
    const path = module.path.startsWith("/") ? module.path : `/${module.path}`;
    window.location.href = `${url}${path}${hash}`;
    return;
  }

  // Sistema tab-based: leva usuário + aba pelo hash.
  const parts: string[] = [];
  if (sso) parts.push(sso);
  if (module) parts.push(`tab=${encodeURIComponent(module.key)}`);
  const hash = parts.length ? `#${parts.join("&")}` : "";
  window.location.href = `${url}/${hash}`;
}
